import { useEffect, useMemo, useRef, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Mail, RotateCcw, Save, Send, Eye, CheckCircle2, UploadCloud, FileText, Trash2, HardHat } from "lucide-react";

function ContractUpload({ endpoint = "/settings/onboarding-contract", title = "Standard Contractor Agreement",
  description = "Store the standard contract PDF once — it's attached automatically to every contractor onboarding email.", testid = "onboarding-contract-card" }) {
  const [contract, setContract] = useState(null);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const loadContract = () => api.get(endpoint).then((r) => setContract(r.data)).catch(() => {});
  useEffect(() => { loadContract(); }, [endpoint]);
  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) { toast.error("Please upload a PDF file."); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(endpoint, fd); toast.success("Standard agreement uploaded."); loadContract(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const remove = async () => {
    try { await api.delete(endpoint); toast.success("Agreement removed."); loadContract(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onDrop = (e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm space-y-4" data-testid={testid}>
      <div>
        <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><HardHat className="w-5 h-5" />{title}</h3>
        <p className="text-sm text-slate-500 mt-0.5">{description}</p>
      </div>
      {contract?.has_file ? (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4" data-testid="contract-current">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-white border border-emerald-200 flex items-center justify-center shrink-0"><FileText className="w-5 h-5 text-emerald-600" /></div>
            <div className="min-w-0">
              <p className="font-medium text-navy truncate flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />{contract.filename}</p>
              <p className="text-xs text-slate-500">Uploaded {contract.uploaded_at?.slice(0, 10)}{contract.uploaded_by ? ` · ${contract.uploaded_by}` : ""}</p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <a href={`${API}${endpoint}/file?auth=${localStorage.getItem("ts_token")}`} target="_blank" rel="noreferrer" data-testid="contract-view" className="text-blue-700 text-sm font-medium inline-flex items-center gap-1 px-2 py-1.5"><FileText className="w-4 h-4" />View</a>
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="contract-replace">Replace</Button>
            <Button variant="outline" size="sm" onClick={remove} data-testid="contract-remove" className="border-red-300 text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
          </div>
        </div>
      ) : (
        <div data-testid="contract-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={`rounded-xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${drag ? "border-navy bg-navy/5" : "border-slate-300 hover:border-navy/40 hover:bg-slate-50"}`}>
          <UploadCloud className={`w-10 h-10 mx-auto mb-3 ${drag ? "text-navy" : "text-slate-400"}`} />
          <p className="text-navy font-medium">{uploading ? "Uploading…" : "Drag & drop the agreement PDF here"}</p>
          <p className="text-sm text-slate-500 mt-1">or click to browse — PDF only</p>
        </div>
      )}
      <input ref={fileRef} type="file" accept="application/pdf" className="hidden" data-testid="contract-file-input" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}

function SwmsUpload() {
  const [doc, setDoc] = useState(null);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = () => api.get("/settings/swms").then((r) => setDoc(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) { toast.error("Please upload a PDF file."); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/settings/swms", fd); toast.success(`SWMS uploaded (v${r.data.version}).`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const remove = async () => {
    try { await api.delete("/settings/swms"); toast.success("SWMS removed."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onDrop = (e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm space-y-4" data-testid="swms-card">
      <div>
        <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><FileText className="w-5 h-5" />Company SWMS (Safe Work Method Statement)</h3>
        <p className="text-sm text-slate-500 mt-0.5">Upload once — the latest approved SWMS is auto-attached to every Job Booking Confirmation, and contractors/employees can view it from their dashboard &amp; must agree to it before starting a job.</p>
      </div>
      {doc?.has_file ? (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4" data-testid="swms-current">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-white border border-emerald-200 flex items-center justify-center shrink-0"><FileText className="w-5 h-5 text-emerald-600" /></div>
            <div className="min-w-0">
              <p className="font-medium text-navy truncate flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />{doc.filename} <span className="text-xs bg-navy text-white px-1.5 py-0.5 rounded-full">v{doc.version}</span></p>
              <p className="text-xs text-slate-500">Uploaded {doc.uploaded_at?.slice(0, 10)}{doc.uploaded_by ? ` · ${doc.uploaded_by}` : ""}</p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <a href={`${API}/swms/file?auth=${localStorage.getItem("ts_token")}`} target="_blank" rel="noreferrer" data-testid="swms-view" className="text-blue-700 text-sm font-medium inline-flex items-center gap-1 px-2 py-1.5"><FileText className="w-4 h-4" />View</a>
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="swms-replace">Replace</Button>
            <Button variant="outline" size="sm" onClick={remove} data-testid="swms-remove" className="border-red-300 text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
          </div>
        </div>
      ) : (
        <div data-testid="swms-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={`rounded-xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${drag ? "border-navy bg-navy/5" : "border-slate-300 hover:border-navy/40 hover:bg-slate-50"}`}>
          <UploadCloud className={`w-10 h-10 mx-auto mb-3 ${drag ? "text-navy" : "text-slate-400"}`} />
          <p className="text-navy font-medium">{uploading ? "Uploading…" : "Drag & drop the SWMS PDF here"}</p>
          <p className="text-sm text-slate-500 mt-1">or click to browse — PDF only</p>
        </div>
      )}
      <input ref={fileRef} type="file" accept="application/pdf" className="hidden" data-testid="swms-file-input" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}

export default function EmailTemplates() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoOn, setAutoOn] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");

  const loadList = () => api.get("/email-templates").then((r) => {
    setList(r.data);
    setSelected((cur) => cur || r.data[0]?.key);
  }).finally(() => setLoading(false));

  useEffect(() => {
    loadList();
    api.get("/settings/reminders").then((r) => setAutoOn(r.data.auto_reminders_enabled)).catch(() => {});
    api.get("/auth/me").then((r) => setAdminEmail(r.data.email || "")).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const t of list) { (g[t.category] = g[t.category] || []).push(t); }
    return g;
  }, [list]);

  const toggleAuto = (v) => api.put("/settings/reminders", { auto_reminders_enabled: v })
    .then((r) => { setAutoOn(r.data.auto_reminders_enabled); toast.success(`Automatic reminders ${r.data.auto_reminders_enabled ? "enabled" : "disabled"}.`); });

  return (
    <div className="space-y-6 animate-fade-up" data-testid="email-templates-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center"><Mail className="w-6 h-6 text-emerald-600" /></div>
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">Email &amp; Communications</h1>
          <p className="text-slate-500 mt-0.5">Customise every email your business sends — subjects, wording, merge fields &amp; attachments, all in one place.</p>
        </div>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm">Loading templates…</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          <aside className="space-y-5" data-testid="template-list">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">{cat}</p>
                <div className="space-y-1">
                  {items.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setSelected(t.key)}
                      data-testid={`template-item-${t.key}`}
                      className={`w-full text-left px-3 py-2.5 rounded-xl border text-sm transition-colors flex items-center justify-between gap-2 ${selected === t.key ? "bg-navy text-white border-navy" : "bg-white border-slate-200 hover:border-navy/30 text-navy"}`}
                    >
                      <span className="truncate">{t.name}</span>
                      {t.customised && <span className={`text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${selected === t.key ? "bg-white/20 text-white" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>Edited</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </aside>

          <div className="min-w-0">
            {selected && (
              <TemplateEditor
                key={selected}
                tplKey={selected}
                adminEmail={adminEmail}
                autoOn={autoOn}
                onToggleAuto={toggleAuto}
                onSaved={loadList}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ tplKey, adminEmail, autoOn, onToggleAuto, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState("");

  const load = () => api.get(`/email-templates/${tplKey}`).then((r) => {
    setData(r.data); setPreview(null);
  }).finally(() => setLoading(false));
  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [tplKey]);
  useEffect(() => { setTo(adminEmail); }, [adminEmail]);

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const insertField = (f) => {
    const token = `{{${f}}}`;
    setData((d) => ({ ...d, body: (d.body || "") + token }));
  };

  const save = async () => {
    if (!data.subject?.trim() || !data.body?.trim()) { toast.error("Subject and body are required."); return; }
    setSaving(true);
    try {
      const payload = { subject: data.subject, body: data.body,
        include_invoice_pdf: data.include_invoice_pdf, include_docket_pdfs: data.include_docket_pdfs, active: data.active };
      const r = await api.put(`/email-templates/${tplKey}`, payload);
      setData(r.data); toast.success("Template saved."); onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    try { const r = await api.post(`/email-templates/${tplKey}/reset`); setData(r.data); setPreview(null); toast.success("Reset to default."); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doPreview = async () => {
    try { const r = await api.get(`/email-templates/${tplKey}/preview`); setPreview(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendPreview = async () => {
    if (!to?.trim()) { toast.error("Enter a recipient email."); return; }
    setSending(true);
    try { const r = await api.post(`/email-templates/${tplKey}/test`, { to }); toast.success(r.data.message || "Preview sent."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (loading || !data) return <p className="text-slate-400 text-sm">Loading…</p>;

  const showAttachments = tplKey === "invoice" || tplKey === "payment_reminder";
  const showAutoReminder = tplKey === "payment_reminder";
  const showContract = tplKey === "contractor_onboarding";
  const showEmployeeContract = tplKey === "employee_onboarding";
  const showSwms = tplKey === "job_confirmation";

  return (
    <div className="space-y-5" data-testid={`template-editor-${tplKey}`}>
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading font-semibold text-navy text-lg">{data.name}</h3>
            <p className="text-sm text-slate-500 mt-0.5">{data.category}{data.customised ? " · customised" : " · using default wording"}</p>
          </div>
          <label className="flex items-center gap-2 shrink-0 cursor-pointer" title="Enable/disable this email">
            <span className={`text-xs font-semibold ${data.active ? "text-emerald-600" : "text-slate-400"}`}>{data.active ? "Active" : "Off"}</span>
            <input type="checkbox" checked={!!data.active} onChange={(e) => setField("active", e.target.checked)} data-testid="template-active-toggle" className="w-4 h-4" />
          </label>
        </div>

        {showAutoReminder && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-start justify-between gap-4" data-testid="auto-reminder-toggle-card">
            <div>
              <p className="font-heading font-semibold text-navy text-sm">Automatic Reminders</p>
              <p className="text-xs text-slate-500 mt-0.5 max-w-md">Off by default — reminders are only sent when <span className="font-semibold">you</span> click "Send Reminder". Turn on to let the system automatically chase overdue invoices &amp; unsigned dockets.</p>
            </div>
            <label className="flex items-center gap-2 shrink-0 cursor-pointer">
              <span className={`text-xs font-semibold ${autoOn ? "text-emerald-600" : "text-slate-400"}`}>{autoOn ? "On" : "Off"}</span>
              <input type="checkbox" checked={autoOn} onChange={(e) => onToggleAuto(e.target.checked)} data-testid="auto-reminder-toggle" className="w-4 h-4" />
            </label>
          </div>
        )}

        <div>
          <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Subject</label>
          <Input data-testid="template-subject" value={data.subject} onChange={(e) => setField("subject", e.target.value)} className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Body</label>
          <textarea data-testid="template-body" value={data.body} onChange={(e) => setField("body", e.target.value)}
            rows={16} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy font-mono leading-relaxed" />
        </div>

        <div className="text-xs text-slate-500">Merge fields (click to insert into body):
          <span className="flex flex-wrap gap-1.5 mt-1.5" data-testid="merge-fields">
            {(data.merge_fields || []).map((f) => (
              <button key={f} onClick={() => insertField(f)} data-testid={`merge-field-${f}`}
                className="bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-navy hover:bg-navy hover:text-white transition-colors">{`{{${f}}}`}</button>
            ))}
          </span>
        </div>

        {showAttachments && (
          <div className="flex flex-wrap gap-4 pt-1">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={!!data.include_invoice_pdf} onChange={(e) => setField("include_invoice_pdf", e.target.checked)} data-testid="attach-invoice-pdf" className="w-4 h-4" />
              Attach Invoice PDF
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={!!data.include_docket_pdfs} onChange={(e) => setField("include_docket_pdfs", e.target.checked)} data-testid="attach-docket-pdfs" className="w-4 h-4" />
              Attach Docket PDFs
            </label>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <Button onClick={save} disabled={saving} className="bg-navy hover:bg-navy/90" data-testid="template-save"><Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save Template"}</Button>
          <Button variant="outline" onClick={reset} data-testid="template-reset"><RotateCcw className="w-4 h-4 mr-2" />Reset to Default</Button>
          <Button variant="outline" onClick={doPreview} data-testid="template-preview"><Eye className="w-4 h-4 mr-2" />Preview</Button>
        </div>
      </div>

      {showContract && <ContractUpload />}
      {showEmployeeContract && <ContractUpload endpoint="/settings/employee-contract" title="Standard Employee Agreement"
        description="Store the standard employee agreement PDF once — it's attached automatically to every employee onboarding email." testid="employee-contract-card" />}
      {showSwms && <SwmsUpload />}

      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm space-y-3" data-testid="send-preview-card">
        <div>
          <h4 className="font-heading font-semibold text-navy flex items-center gap-2"><Send className="w-4 h-4" />Send myself a preview</h4>
          <p className="text-xs text-slate-500 mt-0.5">Sends a live sample of this email (with placeholder data) so you can see exactly how it looks in an inbox.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input data-testid="preview-recipient" value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@company.com" className="sm:max-w-xs" />
          <Button onClick={sendPreview} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-preview-btn"><Send className="w-4 h-4 mr-2" />{sending ? "Sending…" : "Send Preview"}</Button>
        </div>
      </div>

      {preview && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm space-y-3" data-testid="preview-panel">
          <div className="flex items-center gap-2 text-sm text-slate-500"><CheckCircle2 className="w-4 h-4 text-emerald-600" />Live preview</div>
          <div className="text-sm"><span className="text-slate-400">Subject:</span> <span className="font-semibold text-navy" data-testid="preview-subject">{preview.subject}</span></div>
          <div className="border border-slate-100 rounded-xl p-3 bg-slate-50" dangerouslySetInnerHTML={{ __html: preview.html }} data-testid="preview-html" />
        </div>
      )}
    </div>
  );
}
